import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Globe, CheckCircle2, AlertCircle, Trash2, Copy, RefreshCcw, Link2, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

const Pill = ({ ok }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider ${
    ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
  }`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
    {ok ? "Verified" : "Pending"}
  </span>
);

const copy = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

export default function SendingDomainsPage() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ domain: "", selector: "mailwave", is_org_shared: true });
  const [details, setDetails] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [tracking, setTracking] = useState(null);
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [sslStatus, setSslStatus] = useState(null);
  const [issuingSsl, setIssuingSsl] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data }, trackingRes] = await Promise.all([
      api.get("/sending-domains"),
      api.get("/sending-domains/tracking").catch(() => ({ data: null })),
    ]);
    setItems(data);
    if (trackingRes.data) {
      setTracking(trackingRes.data);
      setTrackingInput(trackingRes.data.domain || "");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const hasTeamDomain = items.some((d) => d.is_shared_with_me);
  const hasOwnTeamShare = items.some((d) => d.is_org_shared && d.can_manage);

  useEffect(() => {
    if (!tracking?.domain || !isAdmin) {
      setSslStatus(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/admin/ssl/status/${encodeURIComponent(tracking.domain)}`);
        setSslStatus(data);
      } catch {
        setSslStatus(null);
      }
    })();
  }, [tracking?.domain, isAdmin]);

  const issueTrackingSsl = async () => {
    if (!tracking?.domain) return;
    setIssuingSsl(true);
    try {
      const { data } = await api.post("/admin/ssl/issue", { hostname: tracking.domain });
      setSslStatus(data);
      toast.success(`SSL issued for ${tracking.domain}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SSL issuance failed");
    } finally {
      setIssuingSsl(false);
    }
  };

  const create = async () => {
    if (!form.domain.trim()) return toast.error("Domain required");
    try {
      const { data } = await api.post("/sending-domains", form);
      toast.success("Domain added — copy DNS records below");
      setOpen(false);
      setForm({ domain: "", selector: "mailwave", is_org_shared: true });
      setDetails(data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const refreshSpf = async (id) => {
    try {
      const { data } = await api.post(`/sending-domains/${id}/refresh-records`);
      toast.success("SPF record updated for your active delivery server");
      setDetails(data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    }
  };

  const verify = async (id) => {
    setVerifying(id);
    try {
      const { data } = await api.post(`/sending-domains/${id}/verify`);
      if (data.all_verified) toast.success("All records verified");
      else toast.warning("Some records not found in DNS yet — propagation can take up to 24h");
      load();
    } catch (err) {
      toast.error("Verify failed");
    } finally {
      setVerifying(null);
    }
  };

  const saveTracking = async () => {
    setTrackingSaving(true);
    try {
      const { data } = await api.put("/sending-domains/tracking", { domain: trackingInput.trim() });
      setTracking(data);
      setTrackingInput(data.domain || "");
      toast.success(data.domain ? "Tracking domain saved — add the DNS record below" : "Tracking domain removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save tracking domain");
    } finally {
      setTrackingSaving(false);
    }
  };

  const clearTracking = async () => {
    setTrackingSaving(true);
    try {
      const { data } = await api.put("/sending-domains/tracking", { domain: "" });
      setTracking(data);
      setTrackingInput("");
      toast.success("Tracking domain removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to clear tracking domain");
    } finally {
      setTrackingSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete sending domain?",
      description: "Delete this sending domain? You'll lose its DKIM key.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/sending-domains/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div data-testid="domains-page">
      <PageHeader
        title="Sending domains"
        subtitle="Authenticate your domains with SPF, DKIM and DMARC to improve deliverability."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="add-domain-button">
                <Plus className="h-4 w-4 mr-2" /> Add domain
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add a sending domain</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Domain</Label>
                  <Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="mt-2 rounded-sm" placeholder="yourcompany.com" data-testid="domain-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">DKIM selector</Label>
                  <Input value={form.selector} onChange={(e) => setForm({ ...form, selector: e.target.value })} className="mt-2 rounded-sm" data-testid="domain-selector-input" />
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Used in <code>selector._domainkey.yourdomain.com</code>. Default is fine.</p>
                </div>
                <div className="flex items-start gap-3 rounded-sm border border-neutral-200 dark:border-neutral-800 p-3">
                  <Switch
                    checked={form.is_org_shared}
                    onCheckedChange={(v) => setForm({ ...form, is_org_shared: v })}
                    data-testid="domain-org-shared"
                  />
                  <div>
                    <div className="text-sm font-semibold">Share with team</div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                      When your account belongs to an organization, other members can send from this domain without adding it again.
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={create} className="rounded-sm btn-brand font-semibold" data-testid="domain-create-submit">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-6">
        {(hasTeamDomain || hasOwnTeamShare) && (
          <div className="rounded-sm border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-4 text-sm text-blue-950 dark:text-blue-100">
            {hasTeamDomain && !hasOwnTeamShare
              ? "This account uses a team sending domain shared by your organization. DNS is managed by whoever added the domain."
              : "Team domains are shared with all users in your organization. Add the domain once — everyone can send from it."}
          </div>
        )}
        <section className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid="tracking-domain-section">
          <div className="flex items-center gap-3 mb-4">
            <Link2 className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
            <div>
              <h3 className="font-display font-bold text-xl tracking-tight">Tracking domain</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Open and click links in emails use this host instead of the default Mailwave URL.
                Log in to the app using your main Mailwave URL, not the tracking host.
                {tracking?.source === "organization" && (
                  <span className="block mt-1 text-blue-700 dark:text-blue-300">Using your organization&apos;s default tracking domain.</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="flex-1">
              <Label className="text-xs font-bold uppercase tracking-wider">Tracking host</Label>
              <Input
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                className="mt-2 rounded-sm"
                placeholder="track.yourcompany.com"
                data-testid="tracking-domain-input"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={saveTracking}
                disabled={trackingSaving}
                className="rounded-sm btn-brand font-semibold"
                data-testid="tracking-domain-save"
              >
                <Save className="h-4 w-4 mr-2" />
                {trackingSaving ? "Saving…" : "Save"}
              </Button>
              {tracking?.domain && (
                <Button
                  variant="outline"
                  className="rounded-sm"
                  onClick={clearTracking}
                  disabled={trackingSaving}
                  data-testid="tracking-domain-clear"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          {tracking?.records?.cname && (
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-sm overflow-hidden">
              <div className="px-4 py-2 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
                <div className="label-overline">CNAME record</div>
              </div>
              <div className="p-4 grid grid-cols-12 gap-3 text-sm">
                <div className="col-span-12 md:col-span-4 break-all">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Host / name</div>
                  <div className="font-mono">{tracking.records.cname.host}</div>
                </div>
                <div className="col-span-12 md:col-span-1">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Type</div>
                  <div className="font-mono">{tracking.records.cname.type}</div>
                </div>
                <div className="col-span-12 md:col-span-6 break-all">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Value</div>
                  <div className="font-mono text-xs">{tracking.records.cname.value}</div>
                </div>
                <div className="col-span-12 md:col-span-1 flex md:justify-end items-end">
                  <button onClick={() => copy(tracking.records.cname.value)} className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          {tracking?.records?.note?.value && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3">{tracking.records.note.value}</p>
          )}
          {tracking?.platform_target && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
              Points to Mailwave server: <span className="font-mono">{tracking.platform_target}</span>
            </p>
          )}
          {tracking?.domain && (
            <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-4 text-xs text-amber-950 space-y-3">
              <p className="font-semibold">HTTPS for tracking links</p>
              <p>
                After the CNAME is live, issue a certificate for{" "}
                <span className="font-mono">{tracking.domain}</span>.
                Using Cloudflare proxy? SSL at Cloudflare may be enough — set mode to <strong>Full (strict)</strong>.
              </p>
              {isAdmin && (
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider ${
                    sslStatus?.ssl_ok ? "bg-emerald-100 text-emerald-800" : "bg-white dark:bg-neutral-950/80 text-amber-900"
                  }`}>
                    {sslStatus?.ssl_ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                    {sslStatus?.ssl_ok ? "SSL active" : "SSL not detected"}
                  </span>
                  <Button
                    size="sm"
                    onClick={issueTrackingSsl}
                    disabled={issuingSsl}
                    className="rounded-sm btn-brand font-semibold h-8"
                    data-testid="tracking-issue-ssl"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    {issuingSsl ? "Issuing…" : "Generate SSL"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>

        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <Globe className="h-10 w-10 text-neutral-300 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">Authenticate your first domain</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-md mx-auto">Adding SPF, DKIM and DMARC records lifts inbox placement and prevents spoofing.</p>
            <Button onClick={() => setOpen(true)} className="rounded-sm btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Add domain
            </Button>
          </div>
        ) : (
          items.map((d) => (
            <div key={d.id} className="lift-card bg-white dark:bg-neutral-950 p-6 rounded-sm" data-testid={`domain-card-${d.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                  <h3 className="font-display font-bold text-xl tracking-tight">{d.domain}</h3>
                  <Pill ok={d.verified} />
                  {d.is_org_shared && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
                      {d.is_shared_with_me ? "Team domain" : "Shared with team"}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => refreshSpf(d.id)} data-testid={`domain-refresh-spf-${d.id}`}>
                    <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh SPF
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => verify(d.id)} disabled={verifying === d.id} data-testid={`domain-verify-${d.id}`}>
                    <RefreshCcw className="h-3.5 w-3.5 mr-1" /> {verifying === d.id ? "Verifying..." : "Verify"}
                  </Button>
                  {d.can_manage && (
                    <Button variant="outline" size="sm" className="rounded-sm text-red-600" onClick={() => remove(d.id)} data-testid={`domain-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                {["spf", "dkim", "dmarc"].map((k) => {
                  const rec = d.records?.[k];
                  if (!rec) return null;
                  return (
                    <div key={k} className="border border-neutral-200 dark:border-neutral-800 rounded-sm overflow-hidden">
                      <div className="px-4 py-2 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
                        <div className="label-overline">{k} record</div>
                      </div>
                      <div className="p-4 grid grid-cols-12 gap-3 text-sm">
                        <div className="col-span-12 md:col-span-4 break-all">
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Host / name</div>
                          <div className="font-mono">{rec.host}</div>
                        </div>
                        <div className="col-span-12 md:col-span-1">
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Type</div>
                          <div className="font-mono">{rec.type}</div>
                        </div>
                        <div className="col-span-12 md:col-span-6 break-all">
                          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Value</div>
                          <div className="font-mono text-xs">{rec.value}</div>
                        </div>
                        <div className="col-span-12 md:col-span-1 flex md:justify-end items-end">
                          <button onClick={() => copy(rec.value)} className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50" data-testid={`copy-${k}-${d.id}`}>
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {d.last_checked && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-3">Last checked: {new Date(d.last_checked).toLocaleString()}</div>}
            </div>
          ))
        )}
      </div>

      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>DNS records for {details?.domain}</DialogTitle></DialogHeader>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Add these TXT records at your DNS provider, then click Verify. Propagation can take up to 24h.</p>
          {details && ["spf", "dkim", "dmarc"].map((k) => {
            const r = details.records?.[k];
            if (!r) return null;
            return (
              <div key={k} className="border border-neutral-200 dark:border-neutral-800 rounded-sm p-4">
                <div className="label-overline mb-2">{k}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">Host</div>
                <div className="font-mono text-xs break-all mb-2">{r.host}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">Value</div>
                <div className="font-mono text-xs break-all">{r.value}</div>
                <Button size="sm" variant="outline" className="rounded-sm mt-3" onClick={() => copy(r.value)}>
                  <Copy className="h-3 w-3 mr-1" /> Copy value
                </Button>
              </div>
            );
          })}
          <DialogFooter>
            <Button onClick={() => setDetails(null)} className="rounded-sm btn-brand font-semibold">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
